import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Tooltip, Checkbox } from 'antd';

import {
  MessageOutlined,
  CalendarOutlined,
  SendOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { formatPhoneNumber } from '../../../util/phoneNumber';
import { replyOutcomeText } from '../../../components/referralReplyFax/replyFaxText';
import ReplyFaxNumberChoice from '../../../components/referralReplyFax/ReplyFaxNumberChoice';

// A referral status change can silently send an auto-SMS, fax an auto-reply,
// recompute the due date from default_timeline (blanking it when nil), and
// auto-archive. EMR appointment monitoring is org-level config
// that rarely varies per status, so it is intentionally omitted from these
// hints — surfacing it just adds noise. These helpers turn the remaining
// per-status config (now included in ReferralStatus#object) into user-facing
// hints so none of those side effects are a surprise.

export const willReplaceDueDate = (status, currentDueDate) => {
  if (!status || !currentDueDate) return false;
  const timeline = status.default_timeline;
  if (timeline === null || timeline === undefined) return true; // change blanks the date
  return !moment(currentDueDate).isSame(moment().add(timeline, 'days'), 'day');
};

// replyOutcome: the status row's reply_outcome, present only while the org
// has `referral_reply_fax_approval`. With it the reply gets its own sentence
// (faxing to the number, waiting for a number, or not sent) instead of
// "referral reply successfully sent".
export const buildReferralStatusSuccessMessage = ({
  linkedSmsTemplate,
  linkedReplyTemplate,
  patientPhoneNumber,
  statusChanged = false,
  previousDueDate,
  resultingDueDate,
  dueDatePreserved = false,
  replyOutcome,
}) => {
  const replyReported = replyOutcome !== undefined;
  const replySent = !replyReported && linkedReplyTemplate;
  let successMessage = 'Referral status successfully updated';
  if (linkedSmsTemplate && patientPhoneNumber && replySent) {
    successMessage += ' and SMS + referral reply message successfully sent!';
  } else if (linkedSmsTemplate && patientPhoneNumber) {
    successMessage += ' and SMS message successfully sent!';
  } else if (replySent) {
    successMessage += ' and referral reply successfully sent!';
  } else if (replyReported) {
    successMessage += '.';
  }
  const replySentence = replyReported ? replyOutcomeText(replyOutcome) : null;
  if (replySentence) successMessage += ` ${replySentence}`;
  if (!statusChanged) return successMessage;

  if (dueDatePreserved && resultingDueDate) {
    successMessage += ` Kept your due date (${moment(resultingDueDate).format('MMM D')}).`;
  } else if (resultingDueDate) {
    successMessage += ` Due date set to ${moment(resultingDueDate).format('MMM D')}.`;
  } else if (previousDueDate) {
    successMessage += ' Due date cleared.';
  }

  return successMessage;
};

// Marks the status whose automatic reply is held (reply_held_status_id from
// the referred document status endpoint). Returns new objects, never mutating
// the shared status list.
export const markReplyHeldStatus = (statuses, replyHeldStatusId) => {
  if (!replyHeldStatusId) return statuses;
  return statuses.map((status) => (
    status.id === replyHeldStatusId ? { ...status, reply_held_for_approval: true } : status
  ));
};

// ENG-3859: the appointment placeholders (_app_, _scheduled_provider_) the
// status's automatic reply template uses, or null while the template has not
// been scanned (unknown). The reply worker checks the template itself either way.
// No field at all (an API from before ENG-3859, mid-deploy) asks nothing.
export const replyAppointmentFields = (status) => {
  if (!status?.has_linked_reply_template || status.reply_appointment_fields === undefined) return [];
  return status.reply_appointment_fields;
};

// The placeholders the automatic reply's template is known to use that the
// appointment on file (appointment_time / provider_name) leaves blank. An
// unscanned template counts as using none: never ask for a field the reply
// may not print.
export const missingReplyAppointmentFields = (status, appointment) => (
  (replyAppointmentFields(status) ?? []).filter((field) => (
    field === 'app' ? !appointment?.appointment_time : !appointment?.provider_name
  ))
);

// automaticReply: the referral's reply_fax_destination automatic_reply, when
// the org has `referral_reply_fax_approval`. It replaces "May fax an automated
// reply" with where the reply actually goes.
const replyConsequence = (status, automaticReply) => {
  if (status.reply_held_for_approval || automaticReply?.state === 'choose') {
    return {
      key: 'reply',
      icon: <SendOutlined />,
      shortLabel: 'Reply held',
      label: automaticReply ? 'Faxes a reply. Choose where it goes:' : 'Automated reply is on hold until someone approves the reply fax number',
      tone: 'warning',
      choose: Boolean(automaticReply),
    };
  }
  if (automaticReply?.state === 'confirmed') {
    return {
      key: 'reply',
      icon: <SendOutlined />,
      shortLabel: 'Reply',
      label: `Faxes a reply to ${formatPhoneNumber(automaticReply.destination)}`,
      tone: 'info',
    };
  }
  if (automaticReply?.state === 'not_set_up') {
    return {
      key: 'reply',
      icon: <SendOutlined />,
      shortLabel: 'No reply',
      label: 'No automatic reply: this line isn\'t set up for replies',
      tone: 'info',
    };
  }
  return {
    key: 'reply',
    icon: <SendOutlined />,
    shortLabel: 'Reply',
    label: 'May fax an automated reply to the referring office',
    tone: 'info',
  };
};

export const getStatusConsequences = (
  status,
  { currentDueDate, patientPhoneNumber, automaticReply } = {},
) => {
  if (!status) return [];
  const consequences = [];

  if (status.has_linked_sms_template) {
    let label = 'Texts the patient automatically';
    if (patientPhoneNumber === null || patientPhoneNumber === '') {
      label = 'SMS template linked — you\'ll be asked for the patient\'s phone number';
    } else if (patientPhoneNumber === undefined) {
      label = 'Texts the patient automatically (when a phone number is on file)';
    }
    consequences.push({
      key: 'sms',
      icon: <MessageOutlined />,
      shortLabel: 'SMS',
      label,
      tone: 'warning',
    });
  }

  if (status.has_linked_reply_template) {
    // reply_held_for_approval comes from a stored hold: this status's
    // automatic reply is waiting on a reply fax number decision.
    consequences.push(replyConsequence(status, automaticReply));
  }

  const timeline = status.default_timeline;
  if (timeline !== null && timeline !== undefined) {
    const newDate = moment().add(timeline, 'days');
    let label = `Sets due date to ${newDate.format('MMM D')} (${timeline}-day default)`;
    const replaces = currentDueDate && !newDate.isSame(moment(currentDueDate), 'day');
    if (replaces) {
      label += ` — replaces ${moment(currentDueDate).format('MMM D')}`;
    }
    consequences.push({
      key: 'due_date',
      icon: <CalendarOutlined />,
      shortLabel: `${timeline}d`,
      label,
      tone: replaces ? 'warning' : 'info',
    });
  } else if (currentDueDate) {
    consequences.push({
      key: 'due_date',
      icon: <CalendarOutlined />,
      shortLabel: 'Clear due',
      label: `Clears the due date (currently ${moment(currentDueDate).format('MMM D')})`,
      tone: 'warning',
    });
  }

  if (status.auto_archive) {
    consequences.push({
      key: 'archive',
      icon: <FolderOutlined />,
      shortLabel: 'Archive',
      label: 'Archives the record automatically',
      tone: 'info',
    });
  }

  return consequences;
};

// Readable chip strip for dropdown options. Short labels are scannable at a
// glance; the tooltip still carries the full sentence for detail.
export const ReferralStatusConsequenceBadges = ({ status }) => {
  const consequences = getStatusConsequences(status);
  if (consequences.length === 0) return null;

  return (
    <Tooltip
      mouseEnterDelay={0.35}
      mouseLeaveDelay={0}
      title={(
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {consequences.map((item) => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}
    >
      <span
        className="referral-status-consequence-badges"
      >
        {consequences.map((item) => (
          <span
            key={item.key}
            className={`referral-status-consequence-hint referral-status-consequence-hint--${item.key}`}
          >
            <span
              aria-hidden="true"
              className="referral-status-consequence-hint__icon"
            >
              {item.icon}
            </span>
            <span>{item.shortLabel}</span>
          </span>
        ))}
      </span>
    </Tooltip>
  );
};

ReferralStatusConsequenceBadges.propTypes = {
  status: PropTypes.object,
};

ReferralStatusConsequenceBadges.defaultProps = {
  status: null,
};

// "When you set this status" panel for the Update Referral modal. Optionally
// renders a keep-my-due-date checkbox when the change would replace or clear
// a due date that's already on the record.
// replyFax / replyChoice / onReplyChoiceChange: with approval on, a reply that
// needs a number is chosen right here, saved before the status changes.
export const ReferralStatusConsequencePanel = ({
  status,
  currentDueDate,
  patientPhoneNumber,
  showKeepDueDate,
  keepDueDate,
  onKeepDueDateChange,
  replyFax,
  replyChoice,
  onReplyChoiceChange,
  sendToken,
}) => {
  const automaticReply = replyFax?.automatic_reply;
  const consequences = getStatusConsequences(status, { currentDueDate, patientPhoneNumber, automaticReply });
  if (consequences.length === 0) return null;

  const dueDateAffected = showKeepDueDate && willReplaceDueDate(status, currentDueDate);

  return (
    <div
      style={{
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: '6px',
        padding: '10px 12px',
        marginBottom: '16px',
        maxWidth: '400px',
      }}
    >
      <div style={{ fontWeight: 500, marginBottom: '6px', color: '#595959' }}>
        When you set this status:
      </div>
      {consequences.map((item) => (
        <div
          key={item.key}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            padding: '2px 0',
            color: item.tone === 'warning' ? '#ad6800' : '#595959',
          }}
        >
          <span style={{ marginTop: '2px' }}>{item.icon}</span>
          <span style={{ flex: 1 }}>
            {item.label}
            {item.choose && replyChoice && (
              <div style={{ marginTop: '6px', color: '#595959' }}>
                <ReplyFaxNumberChoice
                  destination={replyFax}
                  sendToken={sendToken}
                  value={replyChoice}
                  onChange={onReplyChoiceChange}
                  compact
                />
                {!replyChoice.option && (
                  <div style={{ marginTop: '4px', fontSize: '12px' }}>
                    If you don&apos;t choose, the reply waits until someone does.
                  </div>
                )}
              </div>
            )}
          </span>
        </div>
      ))}
      {dueDateAffected && (
        <div style={{ marginTop: '6px' }}>
          <Checkbox
            checked={keepDueDate}
            onChange={(e) => onKeepDueDateChange(e.target.checked)}
          >
            {`Keep my current due date (${moment(currentDueDate).format('MMM D')})`}
          </Checkbox>
        </div>
      )}
    </div>
  );
};

ReferralStatusConsequencePanel.propTypes = {
  status: PropTypes.object,
  currentDueDate: PropTypes.string,
  patientPhoneNumber: PropTypes.string,
  showKeepDueDate: PropTypes.bool,
  keepDueDate: PropTypes.bool,
  onKeepDueDateChange: PropTypes.func,
  replyFax: PropTypes.object,
  replyChoice: PropTypes.object,
  onReplyChoiceChange: PropTypes.func,
  sendToken: PropTypes.string,
};

ReferralStatusConsequencePanel.defaultProps = {
  status: null,
  currentDueDate: null,
  patientPhoneNumber: undefined,
  showKeepDueDate: false,
  keepDueDate: false,
  onKeepDueDateChange: () => {},
  replyFax: null,
  replyChoice: null,
  onReplyChoiceChange: () => {},
  sendToken: null,
};
